import { type GetMoreDetailsResponse } from '../../../schemas.js';

export type Approver = {
  id: string;
  name: string;
  canBeReminded: boolean;
  status: string;
  operation?: 'or' | 'and' | 'then';
  keepCustomOperation?: boolean;
  approvers?: Approver[];
  approvalType: string;
  updatedAt: string;
};

const getApproversList = (
  approvers: Approver | Approver[] = [],
): Approver[] => {
  if (Array.isArray(approvers)) {
    return approvers;
  }
  if (approvers.operation === 'then') {
    return (
      approvers.approvers?.map((approver: Approver, index: number) =>
        index === 1
          ? {
              ...approver,
              keepCustomOperation: true,
            }
          : approver,
      ) ?? []
    );
  }

  if (approvers.operation !== 'or' && approvers.approvalType !== 'roles') {
    return approvers.approvers ?? [];
  }
  return [approvers];
};
function normalizeApprovers(approvers: Approver | Approver[] = []) {
  const arr = getApproversList(approvers);

  return arr.reduce(
    (
      {
        list,
        orOperationIndex,
      }: { list: Approver[]; orOperationIndex: number | null },
      item,
      index,
    ) => {
      if (
        orOperationIndex === null &&
        item.approvalType === 'operation' &&
        item.operation === 'or'
      ) {
        list.push({
          ...item,
          approvers: item.approvers?.map((i: Approver, indx) => ({
            ...i,
            keepCustomOperation: indx === (item.approvers?.length ?? 0) - 1,
          })),
        });

        orOperationIndex = index;
        return { list, orOperationIndex };
      }
      if (orOperationIndex !== null && item.approvalType !== 'operation') {
        list[orOperationIndex].approvers?.push({
          ...item,
          keepCustomOperation: true,
        });

        return { list, orOperationIndex };
      }
      if (orOperationIndex !== null && item.operation === 'then') {
        orOperationIndex = null;
        list.push(...(item.approvers as Approver[]));
        return { list, orOperationIndex };
      }

      if (
        orOperationIndex !== null &&
        item.approvalType === 'operation' &&
        !item.keepCustomOperation
      ) {
        list[orOperationIndex].approvers?.push(
          ...normalizeApprovers(item.approvers),
        );
        return { list, orOperationIndex };
      }
      if (item.approvalType === 'operation') {
        list.push(...normalizeApprovers(item));
        return { list, orOperationIndex };
      }

      list.push(item);

      return { list, orOperationIndex };
    },
    { list: [], orOperationIndex: null },
  ).list;
}

const getStructuredApprovers = (
  approverList: Approver[] = [],
  groupedData?: {
    operation?: Approver['operation'];
    updatedAt?: Approver['updatedAt'];
  },
) => {
  const list = approverList.reduce(
    (acc: Approver[], approver: Approver, index: number) => {
      const { approvalType, approvers } = approver;

      if (approvalType === 'operation' && approvers?.length) {
        const { list, isUserType } = getStructuredApprovers(
          approvers,
          approver,
        );

        acc.push({
          id: approver.id,
          approvalType,
          name: approver.name,
          operation: groupedData?.operation ?? approver.operation,
          updatedAt: groupedData?.updatedAt ?? approver.updatedAt,
          status: approver.status,
          canBeReminded: approver.canBeReminded,
          approvers: isUserType
            ? list
            : getStructuredApprovers(list, approver).list,
        });

        return acc;
      }
      const isLastItem = approverList.length - 1 === index;

      acc.push({
        id: approver.id,
        approvalType,
        name: approver.name,
        status: approver.status,
        canBeReminded: approver.canBeReminded,
        updatedAt: groupedData?.updatedAt ?? approver.updatedAt,
        ...(isLastItem || !groupedData?.operation
          ? {}
          : {
              operation: approver.keepCustomOperation
                ? approver.operation
                : groupedData.operation,
            }),
      });

      return acc;
    },
    [],
  );

  const isUserType = list.every(
    (approver: Approver) => approver.approvalType !== 'operation',
  );

  return { list, isUserType };
};

export const formatApprovers = (
  approvers: GetMoreDetailsResponse | GetMoreDetailsResponse[],
) => {
  const approverList = normalizeApprovers(approvers as Approver[]);

  const { list } = getStructuredApprovers(approverList);

  return list;
};
